import { deleteField, doc, getDoc, serverTimestamp, Timestamp, updateDoc, writeBatch } from 'firebase/firestore'
import { mealLogFields } from '@/types/mealLog'
import { Budget, MealPlanLifecycle, PlanCompletionReason, type StoredMeal } from '@/types/mealPlan'
import { db } from './config'

// Per-cook state (cooked/skipped + note) lives in the plan doc's `cookState` map, keyed by cookId. Dotted
// field paths merge into the sparse map without clobbering siblings; an absent key means ready/untouched.
export interface CookedDinner {
  mealId: string | null
  note: string
  // When it was cooked: the original date for a cook marked again, or an earlier day the user picked. Absent = now.
  cookedAt?: Date
}

export async function markMealCooked(uid: string, planDocId: string, cookId: string, dinner: CookedDinner): Promise<void> {
  // The scoped rule only checks `cookState is map`, so the nested timestamp needs no rule change.
  const cookedAt = dinner.cookedAt ? Timestamp.fromDate(dinner.cookedAt) : serverTimestamp()
  const batch = writeBatch(db)
  batch.update(doc(db, 'users', uid, 'meal_plan_results', planDocId), {
    [`cookState.${cookId}.lifecycle`]: 'cooked',
    [`cookState.${cookId}.cookedAt`]: cookedAt,
    [`cookState.${cookId}.note`]: dinner.note
  })
  // The meal's log entry for this cook, keyed by cookId. Rules never let `createdAt` change,
  // so an existing entry is updated and only a new one is dated.
  if (dinner.mealId) {
    const entryRef = doc(db, 'users', uid, 'meal_log', cookId)
    const text = dinner.note.trim()
    if ((await getDoc(entryRef)).exists()) {
      batch.update(entryRef, { cooked: true, text: text || deleteField() })
    } else {
      batch.set(entryRef, {
        ...mealLogFields({ mealId: dinner.mealId, cooked: true, text, planId: planDocId, cookId }),
        createdAt: cookedAt
      })
    }
  }
  await batch.commit()
}

export async function markMealSkipped(uid: string, planDocId: string, cookId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { [`cookState.${cookId}.lifecycle`]: 'skipped' })
}

// Cooking mode reached the last step. Deliberately leaves `lifecycle` alone: finished-but-not-marked
// is the signal home's finish nudge reads.
export async function markCookingFinished(uid: string, planDocId: string, cookId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { [`cookState.${cookId}.cookingFinishedAt`]: serverTimestamp() })
}

// Per-dinner note (Past Plans page) — about the cook instance in THIS plan. A cooked dinner's log
// entry follows it; a dinner with no entry keeps the note on the plan only.
export async function setDinnerNote(uid: string, planDocId: string, cookId: string, note: string): Promise<void> {
  const entryRef = doc(db, 'users', uid, 'meal_log', cookId)
  const entry = await getDoc(entryRef)
  const batch = writeBatch(db)
  batch.update(doc(db, 'users', uid, 'meal_plan_results', planDocId), { [`cookState.${cookId}.note`]: note })
  if (entry.exists()) batch.update(entryRef, { text: note.trim() || deleteField() })
  await batch.commit()
}

// Plan-management metadata writes. The plan result doc is otherwise backend-owned,
// but the owner may write these four user-owned fields directly (a scoped Firestore
// rule restricts client updates to exactly name/notes/rating/deletedAt).
export async function renameMealPlan(uid: string, planDocId: string, name: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { name })
}

export async function setMealPlanNotes(uid: string, planDocId: string, notes: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { notes })
}

// `null` clears the rating. NOT 0 — the Firestore rule accepts null or an int 1-5 and would
// reject 0, so the UI's "no rating" value has to be translated on the way out (see updateRating).
export async function setMealPlanRating(uid: string, planDocId: string, rating: number | null): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { rating })
}

// The day the plan's first dinner is cooked — every later dinner's date derives from it.
// Stored as a Timestamp; the rule accepts any timestamp, so a plan can be dated retroactively.
export async function setMealPlanStartDate(uid: string, planDocId: string, startDate: Date): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { startDate: Timestamp.fromDate(startDate) })
}

// Accept a draft plan — the owner-driven draft → active transition. Lifecycle is
// otherwise backend-owned, but a scoped Firestore rule allows the owner to make
// exactly this one transition (draft → active) directly from the client.
export async function acceptMealPlan(uid: string, planDocId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { lifecycle: MealPlanLifecycle.Active })
}

// Complete an active plan — the owner-driven active → complete transition. `reason` distinguishes
// all_cooked from ended_early; the rule can't verify the claim, so callers pass the reason matching their path.
export async function completeMealPlan(uid: string, planDocId: string, reason: PlanCompletionReason): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), {
    lifecycle: MealPlanLifecycle.Complete,
    completionReason: reason,
    completedAt: serverTimestamp()
  })
}

// A plan's cook, in the minimal shape end-early needs. Callers map their view model to
// this so the service stays free of view/persistence coupling. `hasMeal` = the cook
// generated a recipe (mealId set); a failed slot (no meal) isn't cookable.
export interface EndEarlyCook {
  cookId: string
  hasMeal: boolean
  cooked: boolean
  skipped: boolean
}

// The cookIds end-early swept to `skipped`, so `reactivateMealPlan` (the undo) can
// restore exactly those. Restoring = removing the `lifecycle` field: an absent cookState
// entry renders as "ready/untouched", which is what these cooks were before the sweep.
export interface EndedEarlyMemento {
  skipped: string[]
}

// End an active plan early: the remaining ready cooks move to terminal `skipped`, so the finished plan
// holds no upcoming meals. A SINGLE read-free `updateDoc` — that's what makes it durable the instant it's
// called, surviving an immediate navigation or reload. Returns the swept cookIds so undo is exact.
export async function endMealPlanEarly(uid: string, planDocId: string, cooks: EndEarlyCook[]): Promise<EndedEarlyMemento> {
  const skipped = cooks.filter((c) => c.hasMeal && !c.cooked && !c.skipped).map((c) => c.cookId)
  const update: Record<string, unknown> = {
    lifecycle: MealPlanLifecycle.Complete,
    completionReason: PlanCompletionReason.EndedEarly,
    completedAt: serverTimestamp()
  }
  for (const cookId of skipped) update[`cookState.${cookId}.lifecycle`] = 'skipped'
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), update)
  return { skipped }
}

// Undo of `endMealPlanEarly`: complete → active, clearing completion metadata and restoring the swept
// cooks. `deleteField()` on completionReason/completedAt is what the scoped rule keys on to accept the
// otherwise backend-only transition, and it can only reactivate a plan the user ended early.
export async function reactivateMealPlan(uid: string, planDocId: string, memento: EndedEarlyMemento): Promise<void> {
  const update: Record<string, unknown> = {
    lifecycle: MealPlanLifecycle.Active,
    completionReason: deleteField(),
    completedAt: deleteField()
  }
  for (const cookId of memento.skipped) update[`cookState.${cookId}.lifecycle`] = deleteField()
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), update)
}

// Soft delete — keep the doc for auditing/liability; only the admin tool physically
// removes it. Sets deletedAt to the server time, which hides the plan from the UI. A
// single, read-free write, so it commits durably the instant it's called (survives an
// immediate navigation/reload — the same #915-class guarantee as end-early).
export async function softDeleteMealPlan(uid: string, planDocId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { deletedAt: serverTimestamp() })
}

// Undo of `softDeleteMealPlan`: clear the tombstone so the plan reappears. `deleteField()`
// removes deletedAt — the scoped owner-write rule already permits this (the
// `deletedAt == request.time` clause only constrains a write that SETS the field; removing
// it is unconstrained), so restore needs no rule change.
export async function restoreMealPlan(uid: string, planDocId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'meal_plan_results', planDocId), { deletedAt: deleteField() })
}

// Fetch a single meal from the canonical collection. A meal doc EXISTS iff a recipe was successfully
// generated — failed slots write none — so existence IS the validity check. Returns null otherwise.
export async function getMealById(uid: string, mealId: string): Promise<StoredMeal | null> {
  const snap = await getDoc(doc(db, 'users', uid, 'meals', mealId))
  if (!snap.exists()) return null
  return snap.data() as StoredMeal
}

// The budget a plan was generated with, read once from its request doc. Null for plans made before
// budgets existed.
export async function getPlanBudget(uid: string, requestId: string): Promise<Budget | null> {
  const snap = await getDoc(doc(db, 'users', uid, 'meal_plan_requests', requestId))
  const budget: unknown = snap.get('planRequest.budget')
  return Object.values(Budget).find((b) => b === budget) ?? null
}
